import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateCuisineTypeSimpleDetailsMutation } from '../../hooks'

import { cuisineTypeSimpleDetailsFormInitialValues } from './initial-values'

const CuisineTypeSimpleDetailsForm = ({
  id,
  name,
  description,
  synonyms,
  isErrorProne,
  position,
  isExcludedFromSearch,
  isExcludedFromBrowsing,
  maxPosition,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.CUISINE_TYPE.UPDATE_DETAILS,
  ])

  const [
    updateCuisineTypeDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateCuisineTypeSimpleDetailsMutation()

  const initialValues = useMemo(
    () =>
      cuisineTypeSimpleDetailsFormInitialValues({
        description,
        synonyms,
        isErrorProne,
        position,
        isExcludedFromSearch,
        isExcludedFromBrowsing,
      }),
    [
      description,
      isErrorProne,
      isExcludedFromBrowsing,
      isExcludedFromSearch,
      position,
      synonyms,
    ],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateCuisineTypeDetails(id, data),
    [id, updateCuisineTypeDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Cuisine Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, synonyms, isErrorProne, position, resetForm])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editCuisineType"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            name="isErrorProne"
            label="Error Prone"
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app browsing"
            name="isExcludedFromBrowsing"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.CUISINE_TYPE.UPDATE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

CuisineTypeSimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  description: PropTypes.string,
  synonyms: PropTypes.string,
  name: PropTypes.string.isRequired,
  isErrorProne: PropTypes.bool,
  position: PropTypes.number.isRequired,
  maxPosition: PropTypes.number.isRequired,
  isExcludedFromSearch: PropTypes.bool.isRequired,
  isExcludedFromBrowsing: PropTypes.bool.isRequired,
}

CuisineTypeSimpleDetailsForm.defaultProps = {
  description: undefined,
  synonyms: undefined,
  isErrorProne: false,
}

export default CuisineTypeSimpleDetailsForm
